import { forwardRef, useCallback, useMemo, useRef, useState } from 'react';

import { Field } from '@base-ui/react/field';
import { Select as BaseSelect } from '@base-ui/react/select';
import { CaretDownIcon } from 'components/Icon';
import {
  selectCaretGlyphRecipe,
  selectCaretRecipe,
  selectLeadingGlyphRecipe,
  selectListRecipe,
  selectMenuRecipe,
  selectPositionerRecipe,
  selectTriggerRecipe,
} from 'components/Select';
import { Tag } from 'components/Tag';
import {
  textInputAffixRecipe,
  textInputBoxRecipe,
  textInputHelperRecipe,
  textInputLabelRecipe,
  textInputRootRecipe,
} from 'components/TextInput';
import { useInheritedDarkMode } from 'components/Theme';
import { cn } from 'components/utils';

import {
  multiSelectBadgeListRecipe,
  multiSelectBadgesBoxRecipe,
  multiSelectBadgesCaretRecipe,
  multiSelectBadgesLabelRecipe,
  multiSelectBadgesTriggerRecipe,
  multiSelectIndicatorGlyphRecipe,
  multiSelectIndicatorRecipe,
  multiSelectItemIconRecipe,
  multiSelectItemLabelRecipe,
  multiSelectItemRecipe,
  multiSelectItemTrailingRecipe,
  multiSelectOverflowRecipe,
  multiSelectValueRecipe,
} from './MultiSelect.recipe';

import type { MultiSelectOption, MultiSelectProps } from './types';
import type { SelectRootChangeEventDetails } from '@base-ui/react/select';

/**
 * The tick.
 *
 * Figma's own path, in Figma's own 24px frame, so there is no coordinate arithmetic between the file
 * and the code — the same transcription `Checkbox` ships, and it must change with it. The library
 * draws the glyph with round caps and joins at a 1.5px stroke; the exported blob bakes those into a
 * 300-character bezier, so the three-point path is reconstructed rather than copied.
 *
 * `currentColor`, so `multiSelectIndicatorGlyphRecipe` owns the colour.
 */
const CheckIcon = () => (
  <svg aria-hidden="true" fill="none" focusable="false" viewBox="0 0 24 24">
    <path
      d="M16.2 8.85L10.32 15.15L7.8 12.45"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
);

/**
 * The closed field's summary — Figma's `Value` axis, resolved.
 *
 * A free function rather than a hook or a sub-component, because it is a pure map from the selected
 * options to a node and has no state, no effects and nothing to memoise that the caller does not
 * already memoise upstream.
 *
 * `null` for an empty selection in every display, which is the `Value=Empty` cell: the field draws
 * NO value line, and the unfloated label takes that line instead. Returning an empty string here
 * instead would leave `Field` reading the control as filled and shrink the label above nothing.
 */
const renderValue = ({
  display,
  formatValue,
  maxBadges,
  maxLabels,
  selected,
  valueLabels,
}: {
  display: NonNullable<MultiSelectProps['valueDisplay']>;
  formatValue: MultiSelectProps['formatValue'];
  maxBadges: number;
  maxLabels: number;
  selected: MultiSelectOption[];
  valueLabels: MultiSelectProps['valueLabels'];
}) => {
  // Checked before the empty case on purpose: a caller overriding the value line may well want to
  // say something about having chosen nothing ("Everyone"), and swallowing that would make the
  // override useless in the one case it is most likely to be for.
  if (formatValue) return formatValue(selected);

  if (selected.length === 0) return null;

  if (display === 'count') return valueLabels.count(selected.length);

  /**
   * The label list, CAPPED rather than truncated.
   *
   * The set's rule is that this line "must never wrap or truncate mid-word", and a CSS ellipsis is
   * mid-word by definition — so the remainder is collapsed into the same "+N more" string the
   * badges use. One formatter for both displays, so the two cannot drift apart.
   */
  if (display === 'labels') {
    const shown = selected.slice(0, maxLabels);
    const hidden = selected.length - shown.length;
    const list = shown.map((option) => option.label).join(', ');

    return hidden > 0 ? `${list}, ${valueLabels.overflow(hidden)}` : list;
  }

  const shownBadges = selected.slice(0, maxBadges);
  const hiddenBadges = selected.length - shownBadges.length;

  return (
    <span className={multiSelectBadgeListRecipe()} data-part="badges">
      {shownBadges.map((option) => (
        // `size="sm"` is the set's `Tag Sm`, `color="neutral"` its `Type=Neutral`, and there is no
        // `onDelete` — a badge you can dismiss is a Tag Input, not a Multi-select.
        //
        // NOT `disabled`: `Tag` has no disabled state in the library, deliberately, because its
        // colour is its identity. The disabled field dims this whole row instead — see
        // `multiSelectBadgeListRecipe` for why that is the library's answer and not a shortcut.
        <Tag key={option.value} color="neutral" size="sm">
          {option.label}
        </Tag>
      ))}

      {hiddenBadges > 0 && <span className={multiSelectOverflowRecipe()}>{valueLabels.overflow(hiddenBadges)}</span>}
    </span>
  );
};

/**
 * Design System 2.0 multi-select — several choices from a list that does not need to be visible
 * until opened.
 *
 * Ported from the Figma `Multi-select` set (file 5aSkE0U4TeWbuIZDvccHjc, node 1447:135941), read
 * 2026-09-17. Props mirror the Figma variant axes where they are real inputs rather than depictions
 * of state: `position` is the `Position` axis and `valueDisplay` the `Value` axis, while `State`,
 * `isOpen`, `isDisabled` and `hasError` are observed from the DOM because Base UI's `Field` and
 * `Select` already track them.
 *
 * ── When this is the wrong component ───────────────────────────────────────────────────────────
 *
 * From the set's own description, because getting this wrong is the commonest mistake with it:
 *
 *  · two to about five options that can all stay visible → `Checkbox`
 *  · one choice only → `Select`
 *  · values a person TYPES IN or removes one at a time (emails, free-form tags) → a token field,
 *    which is `Tag Input`, not this
 *
 * The one deliberate difference from `Tag Input` is that there is no dismiss ✕ on a badge here. A
 * multi-select badge is a read-only summary of what is ticked in the menu — you deselect in the
 * list, not in the field. That single difference is what separates the two components, so a request
 * for removable chips is a request for the other one.
 *
 * ── What it shares ─────────────────────────────────────────────────────────────────────────────
 *
 * The closed control is the text input's box and the popup is the select's menu, sharing their
 * recipes rather than reproducing their numbers, so a multi-select, a select and a text input stack
 * in one `FieldGroup` without a seam. `MultiSelect.recipe` explains how, and lists the three
 * places the code still differs from the drawing, and why focus and the open state are no longer
 * among them.
 *
 * Theme is not a prop. Colours resolve from the nearest `.dark` ancestor through the token layer,
 * including for the portalled menu, which gets there via `useInheritedDarkMode` — the same hook
 * `Dialog`, `Select` and `Menu` use.
 *
 * Behaviour worth knowing comes free from Base UI and is the main reason this is not hand-rolled:
 * one tab stop, arrow-key navigation, type-to-move-to-option, focus return to the trigger on close
 * however the list was dismissed, `aria-expanded` on the trigger, `aria-multiselectable` on the
 * listbox, and the popup staying OPEN as options are ticked — which is the part a hand-rolled
 * multi-select nearly always gets wrong.
 */
export const MultiSelect = forwardRef<HTMLButtonElement, MultiSelectProps>(
  (
    {
      label,
      labelHidden,
      options,
      valueDisplay = 'count',
      valueLabels,
      maxBadges = 2,
      maxLabels = 2,
      formatValue,
      position,
      leftIcon,
      helperText,
      error,
      disabled,
      menuFooter,
      'data-testid': testId,
      className,
      rootClassName,
      menuClassName,
      open,
      defaultOpen,
      onOpenChange,
      value,
      defaultValue,
      onValueChange,
      ...rest
    },
    ref
  ) => {
    const hasHelper = Boolean(helperText);
    const hasLeftIcon = Boolean(leftIcon);

    /**
     * Whether the value line draws pills, which is the one display that moves the interior — the
     * set's 2px label-to-value gap applies to the badges alone. Read off `valueDisplay` rather than
     * off what `renderValue` produced, so the geometry does not change under a `formatValue` that
     * happens to return text: a caller asking for the badges layout gets the badges layout.
     */
    const isBadges = valueDisplay === 'badges';

    const boxRef = useRef<HTMLDivElement>(null);

    /**
     * Whether the menu is open — tracked only so `useInheritedDarkMode` can re-resolve the colour
     * scheme each time the portal mounts, which is the one thing that hook needs and Base UI does
     * not hand out. `open` wins when the caller controls it, because a controlled parent can open
     * the field without `onOpenChange` ever firing.
     */
    const [uncontrolledOpen, setUncontrolledOpen] = useState(Boolean(defaultOpen));
    const isOpen = open ?? uncontrolledOpen;

    /**
     * The SELECTION is mirrored here for a different reason, and it is the one structural decision
     * in this file.
     *
     * `Select` never needs it: `Select.Value` renders the chosen option's label for it, resolved
     * from `items`. A multi-select's closed control does not show a label — it shows a SUMMARY,
     * which is a count, a capped label list or a row of `Tag` components, and all three need the
     * selected options as data before anything can be drawn. So the value is read here rather than
     * delegated, and `Select.Value` is not used at all.
     *
     * Uncontrolled is mirrored rather than owned: `defaultValue` seeds it, every change updates it
     * AND is forwarded, and a controlled `value` out-ranks it — so a caller that controls the field
     * sees no behaviour from this state at all.
     */
    const [uncontrolledValue, setUncontrolledValue] = useState<string[]>(defaultValue ?? []);
    const selectedValues = value ?? uncontrolledValue;

    /**
     * Selected options in the order `options` DECLARES them, not the order they were clicked.
     *
     * Documented on `formatValue` because it is observable: ticking Email then Name shows
     * "Name, Email" if that is the list order. Click order is an artefact of how someone happened
     * to move the pointer, and a value line that reorders itself as you tick is unreadable.
     */
    const selected = useMemo(
      () => options.filter((option) => selectedValues.includes(option.value)),
      [options, selectedValues]
    );

    /** Pills are on screen, which is when the Badges cell's taller geometry applies. Empty is the Empty cell. */
    const hasBadges = isBadges && selected.length > 0;

    /**
     * The menu is PORTALLED into `<body>`, so it has none of the trigger's ancestors. The token
     * layer resolves colour from position in the DOM (`.dark`, plus the `.ds-select` and
     * `.ds-checkbox` slot classes), which a portal escapes: left alone, a dark page would open a
     * light menu with light tick boxes.
     *
     * `useInheritedDarkMode` is the layer's answer, already proven on `Dialog` and `Select`. The
     * sentinel below marks where this field really lives, and the class comes back to go on the
     * portal — an ANCESTOR of the popup, so the popup carries only its own slot classes and the
     * ordinary `:where(.dark) .ds-select` rule reaches it.
     *
     * Re-resolved on every open rather than only on mount, because `.dark` can be added to an
     * ancestor after this component mounts — Storybook's theme toolbar does exactly that.
     */
    const { sentinelRef, inheritedDarkClass } = useInheritedDarkMode(isOpen);

    const handleOpenChange = useCallback(
      (nextOpen: boolean, eventDetails: SelectRootChangeEventDetails) => {
        setUncontrolledOpen(nextOpen);
        onOpenChange?.(nextOpen, eventDetails);
      },
      [onOpenChange]
    );

    const handleValueChange = useCallback(
      (nextValue: string[], eventDetails: SelectRootChangeEventDetails) => {
        setUncontrolledValue(nextValue);
        onValueChange?.(nextValue, eventDetails);
      },
      [onValueChange]
    );

    const valueContent = renderValue({
      display: valueDisplay,
      formatValue,
      maxBadges,
      maxLabels,
      selected,
      valueLabels,
    });

    return (
      // `invalid` is what puts `data-invalid` on the root and `aria-invalid` on the trigger; every red
      // token keys off it, so the visual and the announced state cannot drift apart.
      <Field.Root
        className={cn(textInputRootRecipe(), 'ds-select', rootClassName)}
        data-open={isOpen || undefined}
        data-testid={testId}
        disabled={disabled}
        invalid={error}
      >
        {/**
         * `multiple` is set here and cannot be unset by a caller — see `types.ts`. `items` still
         * feeds Base UI from the same `options` the list renders, so type-to-select and the
         * listbox's own label resolution cannot disagree with the rows, even though the closed
         * control draws its summary from `selected` rather than from `Select.Value`.
         */}
        <BaseSelect.Root
          defaultOpen={defaultOpen}
          defaultValue={defaultValue}
          disabled={disabled}
          items={options}
          multiple
          open={open}
          value={value}
          onOpenChange={handleOpenChange}
          onValueChange={handleValueChange}
          {...rest}
        >
          {/* `data-part` names the bordered box so a parent can target it and a test can assert on it
              without depending on a styling class. The slot classes sit on the root. */}
          <div
            ref={boxRef}
            className={cn(
              textInputBoxRecipe({ position, hasHelper }),
              multiSelectBadgesBoxRecipe({ badges: hasBadges, hasHelper }),
              className
            )}
            data-part="box"
          >
            {/* The trigger is the FIRST child and pays all the padding itself, so the button fills the
                box and the entire field opens the menu. Reordering these children, or moving the
                padding onto the box, shrinks the hit area to a 20px strip — see `Select.recipe`. */}
            <BaseSelect.Trigger
              ref={ref}
              className={cn(
                selectTriggerRecipe({ hasHelper, hasLeftIcon, labelHidden }),
                multiSelectBadgesTriggerRecipe({ badges: hasBadges, hasHelper, labelHidden: Boolean(labelHidden) })
              )}
            >
              {leftIcon && (
                <span className={cn(textInputAffixRecipe({ side: 'left', labelHidden }), selectLeadingGlyphRecipe())}>
                  {leftIcon}
                </span>
              )}

              {/* Not `Select.Value`: the summary is built from `selected` — see the note above. */}
              <span className={multiSelectValueRecipe({ display: valueDisplay })} data-part="value">
                {valueContent}
              </span>

              {/**
               * The LIBRARY caret, which is what the set draws — its trailing glyph is an instance of
               * the `CaretDown` icon component, not a shape of its own.
               *
               * `Select` draws the same Phosphor `CaretDown` path inline; this imports it from
               * `icons`, which is the rule, so the two carets are one glyph at one size.
               *
               * Colour comes from `selectCaretRecipe` on the field's clock and the 180° flip from
               * `selectCaretGlyphRecipe` on the list's, both shared with `Select`, so opening either
               * field turns its caret the same way.
               */}
              <BaseSelect.Icon
                className={cn(
                  textInputAffixRecipe({ side: 'right' }),
                  selectCaretRecipe(),
                  multiSelectBadgesCaretRecipe({ badges: hasBadges, hasHelper, labelHidden: Boolean(labelHidden) })
                )}
              >
                <CaretDownIcon className={selectCaretGlyphRecipe()} />
              </BaseSelect.Icon>
            </BaseSelect.Trigger>

            {/* Hidden rather than absent: the trigger still has to be named, and a heading above the
                field is not an accessible name. `sr-only` replaces the floating recipe outright
                rather than joining it, because that recipe positions and animates a label this one
                never shows. */}
            <Field.Label
              className={
                labelHidden
                  ? 'sr-only'
                  : cn(
                      textInputLabelRecipe({ hasLeftIcon }),
                      multiSelectBadgesLabelRecipe({ badges: hasBadges, hasHelper })
                    )
              }
            >
              {label}
            </Field.Label>

            {/**
             * Helper text renders inside the border, which is how the design draws the Error cells.
             *
             * `Field.Error` needs `match` to render off a caller-supplied flag — without it, it
             * renders only for Base UI's own constraint validation, which this component does not
             * drive. Either way the text is wired into `aria-describedby`, so it is announced rather
             * than merely visible.
             */}
            {hasHelper && error && (
              <Field.Error className={textInputHelperRecipe()} match>
                {helperText}
              </Field.Error>
            )}
            {hasHelper && !error && (
              <Field.Description className={textInputHelperRecipe()}>{helperText}</Field.Description>
            )}
          </div>

          {/*
            The sentinel. Marks where this field really lives in the React tree so the portal below
            can inherit the ambient colour scheme; `hidden` keeps it out of layout and out of the a11y
            tree. Same shape as `Dialog`'s and `Select`'s.
          */}
          <span ref={sentinelRef} hidden />

          <BaseSelect.Portal className={inheritedDarkClass}>
            {/**
             * The same fixed placement `Select` uses, and for the same reasons.
             *
             * `alignItemWithTrigger` is Base UI's default and is OFF: on, it slides the panel so the
             * selected row's text lands on the trigger's value text, which is incoherent when
             * several rows are selected at once — there is no single row to align to.
             *
             * `sideOffset={4}` puts the panel 4px below the field at the field's own width — the
             * shared popup spacing `Select` and `Menu` moved to on 2026-09-21.
             *
             * `anchor` is the BORDERED BOX rather than the trigger, and that is what makes those
             * numbers land: the trigger sits inside the box's 1px border, so anchoring to it would
             * measure 298x58 where the field is 300x60 and every offset would be derived from the
             * wrong rectangle.
             */}
            <BaseSelect.Positioner
              align="start"
              alignItemWithTrigger={false}
              anchor={boxRef}
              className={selectPositionerRecipe()}
              side="bottom"
              sideOffset={4}
            >
              <BaseSelect.Popup className={cn('ds-select', selectMenuRecipe(), menuClassName)} data-part="menu">
                {/* The 2px row gap lives here rather than on the popup — see `selectListRecipe`. */}
                <BaseSelect.List className={selectListRecipe()}>
                  {options.map(
                    ({ value: optionValue, label: optionLabel, icon, trailing, disabled: optionDisabled }) => (
                      <BaseSelect.Item
                        key={optionValue}
                        // `ds-checkbox` is what puts the tick box's slots in scope — see the note on
                        // `multiSelectIndicatorRecipe` for why both slot classes can sit on one row.
                        className={cn('ds-checkbox', multiSelectItemRecipe())}
                        disabled={optionDisabled}
                        value={optionValue}
                      >
                        {/* Before the icon, because the checkbox is the row's leading affordance and the
                          caller's glyph describes the option rather than its state. */}
                        <span aria-hidden="true" className={multiSelectIndicatorRecipe()} data-part="indicator">
                          <BaseSelect.ItemIndicator className={multiSelectIndicatorGlyphRecipe()}>
                            <CheckIcon />
                          </BaseSelect.ItemIndicator>
                        </span>

                        {icon && <span className={multiSelectItemIconRecipe()}>{icon}</span>}

                        <BaseSelect.ItemText className={multiSelectItemLabelRecipe()}>
                          {optionLabel}
                        </BaseSelect.ItemText>

                        {trailing && (
                          <span className={multiSelectItemTrailingRecipe()} data-part="option-trailing">
                            {trailing}
                          </span>
                        )}
                      </BaseSelect.Item>
                    )
                  )}
                </BaseSelect.List>

                {/* Outside `List` on purpose, so it is not arrow-keyable and cannot be mistaken for a
                    choice — see the `menuFooter` note in `types.ts`. */}
                {menuFooter && <div data-part="menu-footer">{menuFooter}</div>}
              </BaseSelect.Popup>
            </BaseSelect.Positioner>
          </BaseSelect.Portal>
        </BaseSelect.Root>
      </Field.Root>
    );
  }
);
